import currency from "currency.js";
import { ITableColumn } from "../../interfaces/Table/TableColumn";
import { formatSizePrecision } from "../../utils/format";
import { Align, SizeFillCell } from "../shared/Table/style";
import { titleFor } from "./helpers";
import { ColumnContext, SizeColumnOptions, WithDecorations } from "./types";

export function buildSizeColumn<
  T extends WithDecorations<{ amount?: string | number }>
>(
  { t, tooltips }: Pick<ColumnContext, "t" | "tooltips">,
  { priceAccessor }: SizeColumnOptions = {}
): ITableColumn<T> {
  return {
    title: titleFor(t, "size"),
    accessor: "amount" as any,
    align: "right",
    tooltip: tooltips("positionSize"),
    Cell: ({ value, row }) => {
      const sizeText = formatSizePrecision(
        value as any,
        row.original.amountPrecision
      );
      if (!priceAccessor) {
        return <Align align="right">{sizeText}</Align>;
      }
      const amount = Number(value);
      const price = Number((row.original as any)[priceAccessor]);
      const usd =
        Number.isFinite(amount) && Number.isFinite(price) && amount && price
          ? amount * price
          : undefined;
      return (
        <Align align="right">
          <SizeFillCell>
            <span>{sizeText}</span>
            <span>
              {usd === undefined
                ? "-"
                : currency(usd, { precision: 2 }).format()}
            </span>
          </SizeFillCell>
        </Align>
      );
    },
  };
}

export function buildSizeFilledColumn<
  T extends WithDecorations<{
    amount?: string | number;
    filled?: string | number;
  }>
>(
  { t, tooltips }: Pick<ColumnContext, "t" | "tooltips">,
  { priceAccessor }: SizeColumnOptions = {}
): ITableColumn<T> {
  return {
    title: titleFor(t, "size_filled"),
    accessor: "amount" as any,
    align: "right",
    tooltip: tooltips("orderSize"),
    Cell: ({ row }) => {
      const amountText = formatSizePrecision(
        row.original.amount as any,
        row.original.amountPrecision
      );
      const filledText = formatSizePrecision(
        row.original.filled as any,
        row.original.amountPrecision
      );
      const sizeRow = `${amountText} / ${filledText}`;

      if (!priceAccessor) {
        return <Align align="right">{sizeRow}</Align>;
      }
      const amountN = Number(row.original.amount);
      const filledN = Number(row.original.filled);
      const priceN = Number((row.original as any)[priceAccessor]);
      const validPrice = Number.isFinite(priceN) && priceN > 0;
      const validAmount = Number.isFinite(amountN) && amountN > 0;
      const validFilled = Number.isFinite(filledN);
      const showSplit = validFilled && filledN !== amountN;
      const usdLine = (() => {
        if (!validPrice || !validAmount) return undefined;
        const amountUsd = currency(amountN * priceN, { precision: 2 }).format();
        if (!showSplit) return amountUsd;
        const filledUsd = currency(filledN * priceN, { precision: 2 }).format();
        return `${amountUsd} / ${filledUsd}`;
      })();
      return (
        <Align align="right">
          <SizeFillCell>
            <span>{sizeRow}</span>
            <span>{usdLine ?? "-"}</span>
          </SizeFillCell>
        </Align>
      );
    },
  };
}
